/**
 * Facts about the host page that the composition root needs before booting:
 * which experience to show (the editor, the compact editor for phones, or the
 * static summary), and the profile copy already present in the static shell.
 */
import type { EditorLayout } from "../editor/shell/layout";

export type { EditorLayout };

/**
 * - `editor`: the desktop editor.
 * - `compact`: the editor laid out for phones (docs/planning/07-mobile-editor.md).
 * - `fallback`: the static summary; the engine doesn't boot.
 */
export type AppMode = "editor" | "compact" | "fallback";

/** The layout for a mode that runs the editor. */
export function layoutForMode(mode: Exclude<AppMode, "fallback">): EditorLayout {
  return mode === "compact" ? "compact" : "desktop";
}

/** A layout the address asks for (`?layout=compact`), whatever the screen. */
export type LayoutOverride = "compact";

/** Narrowest window that gets the full editor (SPEC §2). */
export const MIN_EDITOR_WIDTH = 900;

export interface ModeInput {
  readonly width: number;
  /** A touch device with no mouse or trackpad (A-35). */
  readonly coarsePointerOnly: boolean;
  readonly layout: LayoutOverride | null;
  /** The browser has a GPU API the editor draws with (WebGL2 or WebGPU). */
  readonly canRender: boolean;
}

/**
 * Desktops (a fine pointer, 900 px and wider) get the editor; phones, touch
 * tablets and narrow windows get the compact editor (A-35 as revised by item
 * 51). A phone that can't draw gets the static summary instead: an editor with
 * no viewport isn't worth its download there. A desktop without a GPU API
 * still gets the editor, on the Null backend, which says why (R-1).
 */
export function detectMode(input: ModeInput): AppMode {
  if (input.layout === "compact") return "compact";
  if (input.width >= MIN_EDITOR_WIDTH && !input.coarsePointerOnly) return "editor";
  return input.canRender ? "compact" : "fallback";
}

/** `?layout=compact` forces the compact editor, for phones and for trying it on desktop. */
export function layoutFromQuery(search: string): LayoutOverride | null {
  return new URLSearchParams(search).get("layout") === "compact" ? "compact" : null;
}

/** Reads the live mode inputs from the browser. */
export function readModeInput(win: Window): ModeInput {
  const coarse = win.matchMedia("(pointer: coarse)").matches;
  const anyFine = win.matchMedia("(any-pointer: fine)").matches;
  return {
    width: win.innerWidth,
    coarsePointerOnly: coarse && !anyFine,
    layout: layoutFromQuery(win.location.search),
    // Whether the API exists, not a context: making one to ask costs a phone memory.
    canRender: "WebGL2RenderingContext" in win || "gpu" in win.navigator,
  };
}

export interface ShellProfile {
  readonly name: string;
  readonly headline: string;
  readonly resumeHref: string;
}

/**
 * The static shell is generated from the seed at build time (R-3), so it's the
 * source of profile copy for the boot screen, which renders before any
 * content has been fetched.
 */
export function readShellProfile(doc: Document, base: string): ShellProfile {
  const text = (selector: string) => doc.querySelector(selector)?.textContent.trim() ?? "";
  const resumeHref =
    doc.querySelector(".kn-shell__resume")?.getAttribute("href") ?? `${base}content/Resume.pdf`;

  return { name: text(".kn-shell__name"), headline: text(".kn-shell__headline"), resumeHref };
}
